import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Title,
  Stack,
  Text,
  Group,
  Button,
  Badge,
  Divider,
  Alert,
  Loader,
  Image,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconAlertCircle, IconArrowLeft, IconAnchor } from '@tabler/icons-react'
import { useBoat } from '../api/boats'
import { useBoatLocations } from '../api/boat-locations'
import { useBoatStops } from '../api/boat-stops'
import BoatTrackMap from '../components/BoatTrackMap'

function getDateRange(lastKnownLocationTime: string | null): [Date, Date] {
  let endDate: Date

  if (lastKnownLocationTime) {
    endDate = new Date(lastKnownLocationTime)
  } else {
    endDate = new Date()
  }

  const startDate = new Date(endDate)
  startDate.setDate(endDate.getDate() - 7)

  return [startDate, endDate]
}

export default function BoatDetailsPage() {
  const { boatId } = useParams<{ boatId: string }>()
  const navigate = useNavigate()
  const { data: boat, isPending, isError, error } = useBoat(Number(boatId))

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])

  // Update date range when boat data loads
  useEffect(() => {
    if (boat?.gps_device?.last_known_location_time) {
      const [start, end] = getDateRange(
        boat.gps_device.last_known_location_time
      )
      setDateRange([start, end])
    } else if (boat) {
      const [start, end] = getDateRange(null)
      setDateRange([start, end])
    }
  }, [boat])

  // Fetch boat locations
  const {
    data: locationsData,
    isPending: locationsPending,
    isError: locationsError,
  } = useBoatLocations(
    Number(boatId),
    dateRange[0] || new Date(),
    dateRange[1] || new Date()
  )

  const locations = locationsData?.boats[0]?.locations || []

  // Fetch boat stops
  const { data: stopsData } = useBoatStops(
    Number(boatId),
    dateRange[0] || new Date(),
    dateRange[1] || new Date()
  )

  const stops = stopsData?.stops || []

  if (isPending) {
    return (
      <Container size={800} my={40}>
        <Stack align="center" justify="center">
          <Loader />
        </Stack>
      </Container>
    )
  }

  if (isError || !boat) {
    return (
      <Container size={800} my={40}>
        <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red">
          {error instanceof Error
            ? error.message
            : 'Failed to load boat details'}
        </Alert>
        <Button mt="md" onClick={() => navigate('/app/profile')}>
          Back to Profile
        </Button>
      </Container>
    )
  }

  return (
    <Container size={800} my={40}>
      <Stack gap="md">
        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/app/profile')}
          >
            Back to Profile
          </Button>
        </Group>

        <Paper withBorder shadow="sm" p="md" radius="md">
          <Stack gap="md">
            {boat.photos && (
              <Image
                src={boat.photos.medium}
                alt={boat.name}
                radius="md"
                h={300}
                fit="cover"
              />
            )}

            <Group justify="space-between" align="start">
              <div>
                <Group gap="xs">
                  <IconAnchor size={24} />
                  <Title order={2}>{boat.name}</Title>
                </Group>
              </div>
              <Badge size="lg" variant="light">
                {boat.year_built}
              </Badge>
            </Group>

            <Divider />

            <Group grow>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Length
                </Text>
                <Text fw={500}>{boat.length}m</Text>
              </Stack>

              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Year Built
                </Text>
                <Text fw={500}>{boat.year_built}</Text>
              </Stack>

              {boat.boat_type_id && (
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    Type ID
                  </Text>
                  <Text fw={500}>{boat.boat_type_id}</Text>
                </Stack>
              )}
            </Group>

            {boat.boat_info_text && (
              <>
                <Divider />
                <div>
                  <Text size="sm" c="dimmed" mb="xs">
                    Description
                  </Text>
                  <div
                    dangerouslySetInnerHTML={{ __html: boat.boat_info_text }}
                  />
                </div>
              </>
            )}

            {boat.gps_device && (
              <>
                <Divider />
                <Paper withBorder p="sm" radius="sm" bg="gray.0">
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text fw={500}>GPS Device</Text>
                      <Badge color="green">Connected</Badge>
                    </Group>

                    <Group gap="xl">
                      <Stack gap="xs">
                        <Text size="sm" c="dimmed">
                          IMEI
                        </Text>
                        <Text size="sm">{boat.gps_device.imei}</Text>
                      </Stack>

                      {boat.gps_device.last_known_location && (
                        <Stack gap="xs">
                          <Text size="sm" c="dimmed">
                            Last Location
                          </Text>
                          <Text size="sm">
                            {boat.gps_device.last_known_location.y.toFixed(6)},{' '}
                            {boat.gps_device.last_known_location.x.toFixed(6)}
                          </Text>
                        </Stack>
                      )}

                      {boat.gps_device.last_known_external_voltage && (
                        <Stack gap="xs">
                          <Text size="sm" c="dimmed">
                            Battery
                          </Text>
                          <Text size="sm">
                            {boat.gps_device.last_known_external_voltage.toFixed(
                              2
                            )}
                            V
                          </Text>
                        </Stack>
                      )}
                    </Group>

                    {boat.gps_device.last_known_location_time && (
                      <Text size="xs" c="dimmed">
                        Last updated:{' '}
                        {new Date(
                          boat.gps_device.last_known_location_time
                        ).toLocaleString()}
                      </Text>
                    )}
                  </Stack>
                </Paper>
              </>
            )}
          </Stack>
        </Paper>

        {boat.gps_device && (
          <Paper withBorder shadow="sm" p="md" radius="md">
            <Stack gap="md">
              <Group justify="space-between" align="flex-end">
                <Title order={4}>Location History</Title>
                <DatePickerInput
                  type="range"
                  label="Date Range"
                  placeholder="Pick dates range"
                  value={dateRange}
                  onChange={setDateRange}
                  maxDate={new Date()}
                  clearable
                />
              </Group>

              {locationsPending ? (
                <Stack align="center" justify="center" h={400}>
                  <Loader />
                  <Text c="dimmed" size="sm">
                    Loading location data...
                  </Text>
                </Stack>
              ) : locationsError ? (
                <Alert
                  icon={<IconAlertCircle size="1rem" />}
                  title="Error"
                  color="red"
                >
                  Failed to load location data
                </Alert>
              ) : (
                <BoatTrackMap locations={locations} stops={stops} />
              )}

              {locations.length > 0 && (
                <Group gap="md" justify="center">
                  <Badge color="green" size="lg" variant="light">
                    Start
                  </Badge>
                  <Text c="dimmed" size="sm">
                    {locations.length} location points
                  </Text>
                  <Badge color="red" size="lg" variant="light">
                    End
                  </Badge>
                </Group>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  )
}
